/* eslint-disable i18n/no-hardcoded-ui-strings -- Exact, development-only fixture copy from the approved support mockup. */
import { Check, ChevronDown, Sparkles } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { SupportPresentationCards } from "./SupportPresentationCards";

const previewMetadata = {
  support_workspace: {
    bundle: {
      count: 3,
      names: ["Jonas P.", "Mira O."],
      separate_href: "/admin?tab=issues",
    },
    ai_action: {
      title: "Detected from photo · thermostat head faulty",
      detail:
        "Heizung Krämer · Tomorrow, 8–12 · Imane will be informed via WhatsApp",
      label: "Book",
      href: "/admin?tab=issues",
    },
  },
};

const previewTickets = [
  {
    name: "Imane Zaghloul",
    subject: "Heating in room is cold",
    time: "2 h",
    assignee: "Arber",
    flag: "Reported 3× · Urgent",
    urgent: true,
    active: true,
  },
  {
    name: "Felix Brandt",
    subject: "Burst pipe in the bathroom",
    time: "02:20",
    assignee: "Arber",
    flag: "Urgent",
    urgent: true,
  },
  {
    name: "Sherif Khairy",
    subject: "October rent",
    time: "26 h",
    flag: "Waiting for 26 h",
    urgent: true,
    unread: true,
  },
  {
    name: "Nina Hartmann",
    subject: "Visitor at the weekend",
    time: "just now",
    unread: true,
  },
  {
    name: "+49 157 3344 1201",
    subject: "Tap is dripping",
    time: "35 min",
    assignee: "Arber",
    unread: true,
  },
  {
    name: "Lukas Weber",
    subject: "Wi-Fi password",
    time: "8 min",
    assignee: "Arber",
    flag: "Reply ready",
    unread: true,
  },
  {
    name: "Daniel Roth",
    subject: "Move-in key handover",
    time: "20 min",
    assignee: "Neel",
    flag: "Next step: Sat, 10.10. · 11:00",
    unread: true,
  },
];

export function SupportDesignRailPreview() {
  return (
    <aside className="support-rail support-design-rail">
      <button type="button" className="support-primary self-start">
        New message
      </button>
      <section>
        <h2 className="support-eyebrow">INBOXES</h2>
        <button type="button" className="support-rail-row">
          <strong>All</strong>
          <span className="support-count">11</span>
        </button>
        <button type="button" className="support-rail-row is-active">
          <strong>Tenants</strong>
          <span className="support-count">7</span>
        </button>
        <button type="button" className="support-rail-row">
          <strong>Owners</strong>
        </button>
        <button type="button" className="support-rail-row">
          <strong>External</strong>
          <span className="support-count">4</span>
        </button>
      </section>
      <section>
        <h2 className="support-eyebrow">ASSIGNED</h2>
        {[
          ["V", "Vivi", "3"],
          ["N", "Neel", "2"],
          ["A", "Arber", "6"],
        ].map(([initial, name, count]) => (
          <button type="button" className="support-rail-row" key={name}>
            <span className="support-team-avatar">{initial}</span>
            <span>{name}</span>
            <span className="support-count">{count}</span>
          </button>
        ))}
      </section>
      <button type="button" className="support-eyebrow text-left">
        MORE&nbsp; ›
      </button>
    </aside>
  );
}

export function SupportDesignListPreview() {
  return (
    <div className="support-design-list">
      <div className="support-list-heading">
        <div className="flex items-center gap-2">
          <h1 className="min-w-0 flex-1 truncate font-semibold">Tenants</h1>
          <button type="button" className="support-text-button">
            Select
          </button>
          <div
            className="support-segment"
            role="group"
            aria-label="Ticket state"
          >
            <button type="button" aria-pressed="true">
              Open
            </button>
            <button type="button" aria-pressed="false">
              Done
            </button>
          </div>
        </div>
        <label className="support-search">
          <span aria-hidden="true">⌕</span>
          <input aria-label="Search messages" placeholder="Search" readOnly />
        </label>
      </div>
      <div className="support-design-ticket-list">
        {previewTickets.map((ticket) => (
          <button
            type="button"
            key={ticket.name}
            className={`support-design-ticket${ticket.active ? " is-active" : ""}`}
          >
            <span className="support-design-ticket-copy">
              <strong>{ticket.name}</strong>
              <span>{ticket.subject}</span>
              {ticket.flag && (
                <strong className={ticket.urgent ? "is-urgent" : ""}>
                  {ticket.flag}
                </strong>
              )}
            </span>
            <span className="support-design-ticket-meta">
              <strong>{ticket.time}</strong>
              {ticket.assignee && <span>{ticket.assignee}</span>}
              {ticket.unread && <span className="support-unread">1</span>}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

/**
 * Development-only visual-review state for the supplied v2.5 reference.
 *
 * Real tickets only show bundle/action cards when their backend metadata
 * contains those states. This fixture makes every reference state visible for
 * local review without inserting fake tenants, messages, or AI output into the
 * database. SupportInbox gates it behind import.meta.env.DEV.
 */
export function SupportDesignPreview() {
  const { t } = useLanguage();

  return (
    <div className="support-ticket-detail support-design-preview flex h-full flex-col">
      <div className="support-detail-header border-b border-border/60 p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <p className="truncate font-semibold text-foreground">
              Imane Zaghloul
            </p>
            <div className="support-contact-context">
              {t("support.contact.tenant", "Tenant")} · Venloer Str. 12 · 6 · 16
              m² ·{" "}
              <span className="font-semibold underline">
                {t(
                  "support.detail.previousCases",
                  "{count} previous cases"
                ).replace("{count}", "2")}
              </span>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-5">
            <button type="button" className="support-preview-assignee">
              {t("support.preview.assignedArber", "Assigned: Arber")}
              <ChevronDown size={15} />
            </button>
            <button type="button" className="support-preview-done">
              <Check size={17} />
              {t("support.list.done", "Done")}
            </button>
          </div>
        </div>
      </div>

      <SupportPresentationCards metadata={previewMetadata} section="bundle" />

      <div className="support-preview-thread">
        <div className="support-preview-incoming support-preview-opening">
          {t(
            "support.preview.openingMessage",
            "Hello, I have a problem. My heating has been completely cold since last night. Could you send someone?"
          )}
        </div>
        <p className="support-preview-meta">
          WhatsApp · {t("support.preview.today", "Today")}, 07:58
        </p>

        <div className="support-preview-photo">
          <span>
            {t("support.preview.photo", "Photo · Radiator thermostat")}
          </span>
        </div>
        <div className="support-preview-incoming support-preview-photo-message">
          {t(
            "support.preview.photoMessage",
            "Here is a photo of the thermostat."
          )}
        </div>
        <p className="support-preview-meta">
          WhatsApp · {t("support.preview.today", "Today")}, 08:01
        </p>

        <SupportPresentationCards
          metadata={previewMetadata}
          section="aiAction"
        />
      </div>

      <div className="support-preview-composer">
        <div className="support-preview-ai-prompt">
          <Sparkles size={15} />
          <span>
            {t(
              "support.ai.writePrompt",
              "What should AI write? e.g. “Technician arrives tomorrow morning”"
            )}
          </span>
        </div>
        <div className="support-preview-reply">
          {t("issueChatThread.writeMessage", "Write a reply …")}
        </div>
        <div className="support-preview-composer-footer">
          <div className="support-preview-mode">
            <strong>✦ {t("support.ai.draft", "AI draft")}</strong>
            <strong className="support-preview-muted">
              {t("issueChatThread.internalNote", "Internal note")}
            </strong>
            <span>
              via <strong>{t("support.channel.email", "Email")}</strong>
            </span>
            <span>{t("support.composer.from", "from")} info@</span>
            <ChevronDown size={15} />
          </div>
          <button
            type="button"
            className="support-primary support-preview-send"
          >
            {t("common.send", "Send")}
          </button>
        </div>
      </div>
    </div>
  );
}
